import {
  type RedirectRequest,
  type PopupRequest,
  type PopupWindowAttributes
} from "@azure/msal-browser";
import { config } from "@/config/environment";
import type {
  AccountInfo,
  EndSessionRequest,
  IPublicClientApplication
} from "@azure/msal-browser";
import { authLogger } from "./auth-logger";

/**
 * MSAL utility functions
 * Simplified for API application scope usage
 */

/**
 * Checks if an error is an account type error (AADSTS500207)
 */
export function isAccountTypeError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes("AADSTS500207") ||
      error.message.includes("account type") ||
      error.message.includes("can't be used for the resource")
    );
  }
  return false;
}

/**
 * Clear MSAL storage
 */
export function clearAllAuthStorage(): void {
  if (typeof window !== "undefined") {
    try {
      // MSAL handles cache clearing automatically when clearCache() is called
      // This function is kept for compatibility but only clears MSAL-specific items
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes("msal")) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));

      authLogger.cache("MSAL storage cleared");
    } catch (error) {
      authLogger.error("Failed to clear MSAL storage", {
        error: String(error)
      });
    }
  }
}

/**
 * Handles AADSTS500207 account type errors by clearing cache and forcing re-authentication
 */
export async function handleAccountTypeError(
  instance: IPublicClientApplication,
  error: unknown
): Promise<void> {
  authLogger.warn("Handling Account Type Error (AADSTS500207)", {
    error: String(error)
  });

  try {
    // Clear all MSAL cache
    await instance.clearCache();

    // Clear local storage
    clearAllAuthStorage();

    authLogger.cache("Cache cleared successfully");
    authLogger.redirect("Redirecting to login with account selection...");

    // Force re-authentication with account selection
    const loginRequest = createRedirectLoginRequest(
      config.msal.scopes,
      config.msal.authority,
      {
        prompt: "select_account",
        extraQueryParameters: {
          prompt: "select_account"
        }
      }
    );

    await instance.loginRedirect(loginRequest);
  } catch (clearError) {
    authLogger.authError(clearError, "handleAccountTypeError");

    // Fallback: redirect to login page
    window.location.href = "/login";
  }
}

/**
 * Gets popup window configuration for centering
 */
export function getPopupWindowConfig(): PopupWindowAttributes {
  const width = 600;
  const height = 550;
  const left = Math.round((window.screen.width - width) / 2);
  const top = Math.round((window.screen.height - height) / 2);
  return {
    popupSize: {
      width,
      height
    },
    popupPosition: {
      left,
      top
    }
  };
}

/**
 * Creates a redirect login request with proper redirect URI
 * Simplified for API application scope
 */
export function createRedirectLoginRequest(
  scopes: string[],
  authority: string,
  additionalParams: Partial<RedirectRequest> = {}
): RedirectRequest {
  return {
    scopes,
    authority,
    redirectUri: config.msal.redirectUri,
    ...additionalParams
  };
}

/**
 * Creates a popup login request
 * Simplified for API application scope
 */
export function createPopupLoginRequest(
  scopes: string[],
  authority: string,
  additionalParams: Partial<PopupRequest> = {}
): PopupRequest {
  return {
    scopes,
    authority,
    prompt: "select_account",
    ...additionalParams
  };
}

/**
 * Creates a logout request
 */
export function createLogoutRequest(): EndSessionRequest {
  return {
    postLogoutRedirectUri: config.msal.postLogoutRedirectUri
  };
}

/**
 * Ensures an active account is set
 */
export function ensureActiveAccount(
  instance: IPublicClientApplication
): AccountInfo | null {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = instance.getAllAccounts();
  if (accounts.length > 0) {
    instance.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
}

/**
 * Clear active account
 */
export function clearActiveAccount(instance: IPublicClientApplication): void {
  instance.setActiveAccount(null);
  authLogger.cache("Active account cleared");
}

/**
 * Log redirect URI configuration for debugging
 */
export function logRedirectUriConfig(): void {
  authLogger.debug("Redirect URI Configuration", {
    redirectUri: config.msal.redirectUri,
    postLogoutRedirectUri: config.msal.postLogoutRedirectUri,
    currentOrigin:
      typeof window !== "undefined" ? window.location.origin : "unknown"
  });
}

/**
 * Log redirect configuration for debugging
 */
export function logRedirectConfig(): void {
  authLogger.debug("Redirect Configuration", {
    authority: config.msal.authority,
    clientId: config.msal.clientId,
    scopes: config.msal.scopes,
    tokenScopes: config.msal.tokenScopes
  });
}

/**
 * Gets a safe return URL from the location search parameters (for deep linking).
 * @see {@link getReturnUrl} in return-url.ts — fail-closed validation.
 */
export { getReturnUrl, isSafeReturnPath } from "./return-url";

